import { Button, Buttons, Flex, Icon, Image, Text } from '@bitrise/bitkit';
import React, { FunctionComponent } from 'react';

import dataAtIds from '../../../../../../dataAtIds.json';
import useDownloadConfigIOS from '../../../../../Settings/useDownloadConfigIOS';

import Guide from '../../Guide';
import GuideCode from '../../GuideCode';
import GuideStep from '../../GuideStep';
import GuideSPM2a from './GuideSPM.2a.png';
import GuideSPM2b from './GuideSPM.2b.png';
import GuideSPM3 from './GuideSPM.3.png';
import GuideSPM4 from './GuideSPM.4.png';
import GuideSPM6 from './GuideSPM.6.png';

const GuideSPM: FunctionComponent = () => {
  const handleDownloadCollectorToken = useDownloadConfigIOS();

  return (
    <Guide data-trace-at-id={dataAtIds.GettingStartedGuideIosSPM} name="spm">
      <GuideStep heading="Sample app" number="1">
        <Text>
          If you&apos;re not using your own app, start off by cloning the iosCCC app in your
          terminal.
        </Text>

        <GuideCode bash copy>
          {`git clone git@github.com:bitrise-io/iosCCC.git
cd iosCCC/`}
        </GuideCode>
      </GuideStep>

      <GuideStep heading="Download Config file" number="2">
        <Text>
          Next we need to add the{' '}
          <Text inline weight="bold">
            bitrise_configuration.plist
          </Text>{' '}
          to your project
        </Text>

        <Flex alignChildren="middle" direction="horizontal" gap="x3">
          <Flex grow initial="none">
            <Text>
              1: Download{' '}
              <Text inline weight="bold">
                bitrise_configuration.plist
              </Text>
            </Text>
          </Flex>

          <Flex>
            <Buttons>
              <Button elevation="x1" onClick={handleDownloadCollectorToken} size="small">
                <Icon name="Download" size="1rem" />
                <Text>Download</Text>
              </Button>
            </Buttons>
          </Flex>
        </Flex>

        <Text>2: Open your project in XCode</Text>

        <GuideCode bash copy>
          open CurrencyConverterCalculator.xcodeproj
        </GuideCode>

        <Text>
          3: Now, drag and drop the bitrise_configuration.plist to the root of your project, in this
          case it directly into the CurrencyConverterCalculator project
        </Text>

        <Image borderRadius="x2" elevation="x1" src={GuideSPM2a} width="251px" />
        <Text>4: If prompted, confirm to add the CurrencyConverterCalculator project</Text>

        <Image borderRadius="x2" elevation="x1" src={GuideSPM2b} width="600px" />

        <Text>5: All done! 🙌</Text>
      </GuideStep>

      <GuideStep heading="Initialise SDK" number="3">
        <Text>Now we are going to install the Trace SDK via Swift Package Manger.</Text>

        <Text>
          1: With your project open in Xcode, go to{' '}
          <Text inline weight="bold">
            File &gt; Swift Packages &gt; Add Package Dependency
          </Text>
        </Text>

        <Text>
          2: Enter the repository URL{' '}
          <Text inline weight="bold">
            https://github.com/bitrise-io/trace-cocoa-sdk.git
          </Text>
          .
        </Text>

        <Image borderRadius="x2" elevation="x1" src={GuideSPM3} width="600px" />

        <Text>
          3: Continue through the prompted package options choosing an appropriate version and
          selecting the correct application target.
        </Text>

        <Text>
          4: Once complete, you should see{' '}
          <Text inline weight="bold">
            trace-cocoa-sdk
          </Text>{' '}
          in your list of Swift Packages.
        </Text>
      </GuideStep>

      <GuideStep heading="Adding Other Linker Flags to application target" number="4">
        <Text>
          1: Select your application project in the Project Navigator (blue project icon) to
          navigate to the target configuration window and select the application target under the
          &ldquo;Targets&rdquo; heading in the sidebar.
        </Text>

        <Text>
          2: In the tab bar at the top of that window, open the &ldquo;Build Settings&rdquo; panel.
        </Text>

        <Image borderRadius="x2" elevation="x1" src={GuideSPM4} width="600px" />

        <Text>
          3: Search for{' '}
          <Text inline weight="bold">
            Other Linker Flags
          </Text>{' '}
          or{' '}
          <Text inline monospace weight="bold">
            OTHER_LDFLAGS
          </Text>{' '}
          and enter
        </Text>

        <GuideCode copy>-ObjC -l z -l c++</GuideCode>
      </GuideStep>

      <GuideStep heading="Creating a Trace object in code" number="5">
        <Text>
          In your project add{' '}
          <Text inline monospace weight="bold">
            import Trace
          </Text>{' '}
          and call{' '}
          <Text inline monospace weight="bold">
            let trace = Trace.shared
          </Text>{' '}
          to start the SDK, like shown below.
        </Text>

        <GuideCode copy={[false, true, false, false, false, false, true]}>
          {`import SwiftUI
import Trace

@main
struct App: SwiftUI.App {
  @StateObject var viewModel = AppViewModel()
  private let trace = Trace.shared
}`}
        </GuideCode>
      </GuideStep>

      <GuideStep heading="Now you can run it!" number="6">
        <Text>All that is left now is to build and run the app in the simulator or a device.</Text>

        <Image borderRadius="x2" elevation="x1" src={GuideSPM6} width="600px" />

        <Text>
          Check for any errors in the logs. Please retry the setup, or contact support if you get
          any of the following errors:
        </Text>

        <GuideCode>
          {`[Bitrise:Trace/internalError] Bitrise configuration file is missing from Bundle.main
[Bitrise:Trace/internalError] Application failed to read configuration file
[Bitrise:Trace/queue] Failed to submit metric, will try again in 1 minute.`}
        </GuideCode>

        <Text>
          Provided there are no errors, your app will be reporting performance data. Feel free to
          play around the app to generate more sample data.
        </Text>
      </GuideStep>
    </Guide>
  );
};

export default GuideSPM;
